import React, { useContext, useState } from "react";
import styled from "styled-components";

import { Button } from "@examspark/common-ui";
import IconFeedbackLine from "@examspark/common-ui/dist/components/icons/IconFeedbackLine";

import { getCurrentItem } from "../../utils/utils";
import IconCalculator from "../common/icons/IconCalculator";
import IconFlag from "../common/icons/IconFlag";
import IconHighlighter from "../common/icons/IconHighlighter";
import IconLineReader from "../common/icons/IconLineReader";
import IconSave from "../common/icons/IconSave";
import IconStrikethrough from "../common/icons/IconStrikethrough";
import IconZoomIn from "../common/icons/IconZoomIn";
import IconZoomOut from "../common/icons/IconZoomOut";

import ContentFeedbackModal from "./ContentFeedbackModal";
import { ExamContext } from "./context/examContext";

const NavigationButton = styled(Button)`
  border-radius: 0;
  color: #333;
  display: flex;
  padding: 0;
  justify-content: center;
  align-items: center;
  height: 4rem;

  svg {
    width: 1.6rem;
    display: block;
    fill: #333;
  }
`;

interface Props {
  isFlagged: boolean;
  toggleFlagged: () => void;

  isCalculatorEnabled: boolean;
  setIsCalculatorEnabled: (isCalculatorEnabled: boolean) => void;

  isLineReaderEnabled: boolean;
  setIsLineReaderEnabled: (isLineReaderEnabled: boolean) => void;

  isHighlighterEnabled: boolean;
  setIsHighlighterEnabled: (isHighlighterEnabled: boolean) => void;

  allowSaveAndExit: boolean;
  saveAndExit: () => void;

  canIncreaseFont: boolean;
  increaseFont: () => void;
  canDecreaseFont: boolean;
  decreaseFont: () => void;

  // 'can' means that the item type supports the action
  // 'allow' below means the feature flag is enabled
  canResponseMask: boolean;
  isResponseMaskEnabled: boolean;
  setIsResponseMaskEnabled: (isStrikethroughEnabled: boolean) => void;

  // feature flags / exam config settings
  allowFeedback?: boolean;
  allowLineReader?: boolean;
  allowHighlighter?: boolean;
  allowResponseMask?: boolean;
  allowCalculator?: boolean;
  previewUserId?: string;
  previewFeedbackHandler?: () => void;
}

const Tools = (props: Props) => {
  const [state] = useContext(ExamContext);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  return (
    <>
      {props.previewUserId && (
        <ContentFeedbackModal
          reporterUserId={props.previewUserId}
          resource={getCurrentItem(state)}
          isOpen={isFeedbackModalOpen}
          onRequestClose={() => setIsFeedbackModalOpen(false)}
        />
      )}
      {props.allowSaveAndExit && (
        <NavigationButton title="Save/Exit" onClick={props.saveAndExit}>
          <IconSave />
        </NavigationButton>
      )}
      <NavigationButton
        title="Increase Font Size"
        onClick={props.increaseFont}
        disabled={!props.canIncreaseFont}
      >
        <IconZoomIn />
      </NavigationButton>
      <NavigationButton
        title="Decrease Font Size"
        onClick={props.decreaseFont}
        disabled={!props.canDecreaseFont}
      >
        <IconZoomOut />
      </NavigationButton>
      {props.allowResponseMask && (
        <NavigationButton
          title="Response Masking"
          onClick={() => {
            props.setIsResponseMaskEnabled(!props.isResponseMaskEnabled);
          }}
          color={props.isResponseMaskEnabled ? "primary" : "secondary"}
          disabled={!props.canResponseMask}
        >
          <IconStrikethrough
            color={props.isResponseMaskEnabled ? "white" : undefined}
          />
        </NavigationButton>
      )}
      {props.allowLineReader && (
        <NavigationButton
          title="Toggle Line Reader"
          color={props.isLineReaderEnabled ? "primary" : "secondary"}
          onClick={() => {
            props.setIsLineReaderEnabled(!props.isLineReaderEnabled);
          }}
        >
          <IconLineReader
            color={props.isLineReaderEnabled ? "white" : undefined}
          />
        </NavigationButton>
      )}
      {props.allowHighlighter && (
        <NavigationButton
          title="Toggle Highlighter"
          onClick={() => {
            props.setIsHighlighterEnabled(!props.isHighlighterEnabled);
          }}
          color={props.isHighlighterEnabled ? "primary" : "secondary"}
        >
          <IconHighlighter
            color={props.isHighlighterEnabled ? "white" : undefined}
          />
        </NavigationButton>
      )}
      {props.allowCalculator && (
        <NavigationButton
          title="Toggle Calculator"
          onClick={() => {
            props.setIsCalculatorEnabled(!props.isCalculatorEnabled);
          }}
          color={props.isCalculatorEnabled ? "primary" : "secondary"}
        >
          <IconCalculator
            color={props.isCalculatorEnabled ? "white" : undefined}
          />
        </NavigationButton>
      )}
      <NavigationButton title="Flag Item" onClick={() => props.toggleFlagged()}>
        <IconFlag />
      </NavigationButton>
      {props.allowFeedback &&
        props.previewUserId &&
        props.previewFeedbackHandler && (
          <>
            <NavigationButton
              title="Feedback"
              onClick={props.previewFeedbackHandler}
            >
              <IconFeedbackLine />
            </NavigationButton>
          </>
        )}
    </>
  );
};

export default Tools;
